import type {
  AuthenticatedUserContract,
  Locale,
  StudentReplicaContract,
} from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button } from "@chams-pay/design-system/components/button";
import { Input } from "@chams-pay/design-system/components/input";
import { Label } from "@chams-pay/design-system/components/label";
import type { PasskeyUnlockMaterial } from "@chams-pay/local-store";
import { Eye, EyeOff, Fingerprint } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import {
  deviceFingerprintLabel,
  type AccessAuthenticatedHandler,
  useAccess,
  useExistingReplica,
  useInstallation,
  useOfflineSetup,
  useUnlock,
} from "../core/use-session.js";
import { Wordmark } from "../ui/brand.js";

type TranslationKey = Parameters<typeof translate>[1];

function stop(event: FormEvent, run: () => void) {
  event.preventDefault();
  run();
}

/* ------------------------------------------------------------------ */
/* Marks                                                               */
/* ------------------------------------------------------------------ */

function PasskeyIcon() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="2.4" />
      <path d="M9 2.2a6.8 6.8 0 0 1 6.8 6.8M9 15.8A6.8 6.8 0 0 1 2.2 9M4.4 4.4A6.5 6.5 0 0 1 9 5.6" />
    </svg>
  );
}

function TickIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 9.3l3.2 3.2L14 5.8" className="[stroke-dasharray:26] animate-tick" />
    </svg>
  );
}

function DeviceIcon() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <rect x="2.5" y="3" width="13" height="9.5" rx="2" />
      <path d="M6 15h6" />
    </svg>
  );
}

function RequesterIcon() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <circle cx="9" cy="6" r="3" />
      <path d="M3 15.5c0-2.8 2.7-4.5 6-4.5s6 1.7 6 4.5" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <rect x="3.5" y="7.5" width="11" height="7.5" rx="2" />
      <path d="M6 7.5V5.6A3 3 0 0 1 12 5.6V7.5" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="6.5" />
      <path d="M9 5.5V9l2.5 1.6" />
    </svg>
  );
}

function BrokenBoxIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <path d="M3 5.5h12v9H3zM6 8.5l6 3M12 8.5l-6 3" />
    </svg>
  );
}

function AlertRoundel({ className }: { readonly className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center rounded-full bg-destructive font-heading font-extrabold text-white ${className ?? "size-[19px] text-xs"}`}
    >
      !
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Sign in                                                             */
/* ------------------------------------------------------------------ */

export function DesktopAccessScreen({
  locale,
  onAuthenticated,
}: {
  readonly locale: Locale;
  readonly onAuthenticated: AccessAuthenticatedHandler;
}) {
  const t = (key: TranslationKey) => translate(locale, key);
  const access = useAccess(onAuthenticated);
  const hasReplica = useExistingReplica();
  const phase = access.phase;

  const heading =
    access.mode === "enroll"
      ? phase === "submitting"
        ? t("entry.enrollTitleSubmit")
        : phase === "error"
          ? t("entry.enrollTitleErr")
          : t("entry.enrollTitleIdle")
      : phase === "submitting"
        ? t("entry.signinTitleSubmit")
        : phase === "error"
          ? t("entry.signinTitleErr")
          : t("entry.signinTitleIdle");
  const subheading =
    access.mode === "enroll"
      ? phase === "submitting"
        ? t("entry.enrollSubSubmit")
        : phase === "error"
          ? t("entry.enrollSubErr")
          : t("entry.enrollSubIdle")
      : phase === "submitting"
        ? t("entry.signinSubSubmit")
        : phase === "error"
          ? t("entry.signinSubErr")
          : t("entry.signinSubIdle");
  const primaryLabel =
    access.mode === "enroll"
      ? phase === "submitting"
        ? t("entry.enrollBtnSubmit")
        : phase === "error"
          ? t(
              access.registrationFallbackReady
                ? "entry.enrollRetryCompatible"
                : "entry.enrollRetry",
            )
          : t("entry.registerPasskey")
      : phase === "submitting"
        ? t("entry.btnSubmit")
        : phase === "error"
          ? t("entry.btnErr")
          : t("entry.btnIdle");
  const errorTitle = t(access.mode === "enroll" ? "entry.enrollErrTitle" : "entry.signinErrTitle");
  const errorBody = t(
    access.mode === "enroll"
      ? access.registrationFallbackReady
        ? "entry.enrollErrCompatibleBody"
        : "entry.enrollErrBody"
      : "entry.signinErrBody",
  );

  return (
    <main className="access-page flex min-h-dvh bg-card" data-testid="login-screen">
      <aside className="relative hidden w-[46%] shrink-0 flex-col overflow-hidden bg-primary-strong p-11 lg:flex">
        <span
          aria-hidden="true"
          className="chams-logo pointer-events-none absolute -bottom-[160px] -end-[140px] size-[520px] bg-white opacity-[0.09] motion-safe:animate-spin motion-safe:[animation-duration:90s]"
        />
        <div className="relative flex items-center gap-3">
          <span aria-hidden="true" className="chams-logo size-[34px] bg-white" />
          <Wordmark className="text-[20px] text-white [&>span]:text-white" />
        </div>
        <div className="relative mt-auto flex flex-col gap-4">
          <h1 className="max-w-[22ch] font-heading text-[30px] font-bold leading-[1.4] text-white">
            {t("entry.heroTitle")}
          </h1>
          <p className="max-w-[40ch] text-sm leading-[1.85] text-white/70">{t("entry.heroBody")}</p>
          <p className="mt-3 flex w-fit items-center gap-2.5 rounded-lg bg-white/10 px-[15px] py-3 text-xs text-white/85">
            <span aria-hidden="true" className="size-[7px] rounded-full bg-[#7de2a8]" />
            {t("entry.heroStatus")}
          </p>
        </div>
        <p className="relative mt-9 text-[11px] text-white/45">
          {t("entry.schoolName")} ·{" "}
          <bdi dir="ltr" className="chams-figure">
            2025–2026
          </bdi>
        </p>
      </aside>

      <section className="flex flex-1 items-center justify-center bg-surface-2 px-6 py-11">
        <div className="flex w-full max-w-[410px] animate-fade-up flex-col gap-[22px]">
          <div className="flex flex-col gap-[7px]" aria-live="polite">
            <h2 className="font-heading text-[22px] font-bold leading-[1.3]">{heading}</h2>
            <p className="text-[12.5px] leading-[1.6] text-muted-foreground">{subheading}</p>
          </div>

          <div
            role="tablist"
            aria-label={t("entry.tabLogin")}
            data-tab-count="2"
            data-active-index={access.mode === "login" ? "0" : "1"}
            className="sliding-tabs flex gap-[3px] overflow-hidden rounded-[9px] bg-sunken p-[3px]"
          >
            <span className="sliding-tabs__indicator" aria-hidden="true" />
            <button
              type="button"
              role="tab"
              aria-selected={access.mode === "login"}
              disabled={access.busy}
              onClick={() => access.setMode("login")}
              className="h-11 min-h-11 flex-1 rounded-md px-3 font-heading text-[12.5px] font-bold whitespace-nowrap outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {t("entry.tabLogin")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={access.mode === "enroll"}
              disabled={access.busy}
              onClick={() => access.setMode("enroll")}
              className="h-11 min-h-11 flex-1 rounded-md px-3 font-heading text-[12.5px] font-bold whitespace-nowrap outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {t("entry.tabRegister")}
            </button>
          </div>

          <form
            className="access-form flex animate-fade-up flex-col gap-4"
            onSubmit={(event) => stop(event, () => void access.submit())}
          >
            {access.mode === "login" ? (
              <>
                <Label className="flex flex-col items-stretch gap-2 text-start">
                  <span className="font-heading text-[11.5px] font-bold text-muted-foreground">
                    {t("entry.username")}
                  </span>
                  <Input
                    className="h-[46px] rounded-[9px] px-[13px] text-[13.5px]"
                    dir="ltr"
                    value={access.identifier}
                    onChange={(event) => access.setIdentifier(event.target.value)}
                    autoComplete="username webauthn"
                    required
                  />
                </Label>
                <div className="flex items-center gap-[13px] rounded-[11px] border border-teal-100 bg-teal-50 p-4">
                  <span className="grid size-11 shrink-0 place-items-center rounded-full bg-card text-primary motion-safe:animate-pulse-ring">
                    <PasskeyIcon />
                  </span>
                  <span className="flex flex-col gap-[3px]">
                    <span className="font-heading text-[13px] font-bold leading-[1.35] text-primary-strong">
                      {t("entry.passkeyTitle")}
                    </span>
                    <span className="text-[11.5px] leading-[1.6] text-teal-600">
                      {t("entry.passkeyBody")}
                    </span>
                  </span>
                </div>
              </>
            ) : (
              <Label className="flex flex-col items-stretch gap-2 text-start">
                <span className="font-heading text-[11.5px] font-bold text-muted-foreground">
                  {t("entry.oneTimeCode")}
                </span>
                <Input
                  className="chams-figure h-14 rounded-[9px] px-4 text-center text-[18px] font-bold tracking-[0.08em]"
                  dir="ltr"
                  value={access.code}
                  onChange={(event) => access.setCode(event.target.value)}
                  autoComplete="one-time-code"
                  spellCheck={false}
                  required
                />
                <small className="text-[11.5px] font-normal leading-[1.6] text-muted-2">
                  {t("entry.codeHint")}
                </small>
              </Label>
            )}

            {phase === "error" && (
              <div
                role="alert"
                data-testid="access-error"
                className="flex animate-shake gap-[11px] rounded-[11px] border border-destructive-border bg-destructive-soft px-[15px] py-[14px]"
              >
                <AlertRoundel />
                <span className="flex flex-col gap-[3px] text-destructive">
                  <span className="font-heading text-[12.5px] font-bold leading-[1.4]">
                    {errorTitle}
                  </span>
                  <span className="text-[11.5px] leading-[1.6]">{errorBody}</span>
                </span>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={access.busy}
              className={`access-submit w-full rounded-[10px] shadow-[0_2px_6px_-2px_rgb(42_138_148_/_0.6)] ${
                phase === "submitting" ? "bg-primary-hover disabled:bg-primary-hover" : ""
              }`}
            >
              {primaryLabel}
            </Button>

            {access.mode === "login" && hasReplica && (
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="-mx-2 inline-flex min-h-11 items-center rounded-md px-2 font-heading text-xs font-bold text-primary outline-none hover:text-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  {t("entry.useOffline")}
                </button>
              </div>
            )}
          </form>

          {access.notice === "enrolled" && (
            <div
              role="status"
              className="flex animate-fade-up items-center gap-3 rounded-[11px] border border-success-border bg-success-soft p-[15px]"
            >
              <span className="grid size-[34px] shrink-0 place-items-center rounded-full bg-success text-white">
                <TickIcon />
              </span>
              <span className="flex flex-col gap-[3px] text-success">
                <span className="font-heading text-[13px] font-bold leading-[1.35]">
                  {t("entry.regDoneTitle")}
                </span>
                <span className="text-[11.5px] leading-[1.55]">{t("entry.regDoneBody")}</span>
              </span>
            </div>
          )}
          {access.notice === "existing-passkey" && (
            <div
              role="status"
              className="flex animate-fade-up items-center gap-3 rounded-[11px] border border-teal-100 bg-teal-50 p-[15px]"
            >
              <span className="grid size-[34px] shrink-0 place-items-center rounded-full bg-primary text-white">
                <PasskeyIcon />
              </span>
              <span className="flex flex-col gap-[3px] text-primary-strong">
                <span className="font-heading text-[13px] font-bold leading-[1.35]">
                  {t("entry.existingPasskeyTitle")}
                </span>
                <span className="text-[11.5px] leading-[1.55]">
                  {t("entry.existingPasskeyBody")}
                </span>
              </span>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Device authorization                                                */
/* ------------------------------------------------------------------ */

function GateCanvas({
  testId,
  children,
}: {
  readonly testId: string;
  readonly children: ReactNode;
}) {
  return (
    <main
      className="centered-gate grid min-h-dvh place-items-center bg-canvas px-6 py-14"
      data-testid={testId}
    >
      {children}
    </main>
  );
}

function FingerprintBlock({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex flex-col gap-[5px] rounded-[9px] border border-sunken bg-surface-2 px-[14px] py-3">
      <span className="chams-eyebrow">{label}</span>
      <bdi
        dir="ltr"
        className="chams-figure text-xs font-semibold leading-[1.5] text-muted-foreground"
      >
        {value}
      </bdi>
    </div>
  );
}

export function DesktopInstallationScreen({
  locale,
  user,
  csrf,
  onReady,
  onLogout,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract;
  readonly csrf: string;
  readonly onReady: (csrf: string) => void;
  readonly onLogout: () => void;
}) {
  const t = (key: TranslationKey) => translate(locale, key);
  const gate = useInstallation(user, csrf, onReady);
  const fingerprint = deviceFingerprintLabel(gate.installation);
  const state = gate.failed
    ? "error"
    : !gate.installation
      ? "new"
      : gate.canAuthorize
        ? "review"
        : "waiting";

  const cardClass =
    state === "error"
      ? "border-destructive-border animate-shake"
      : state === "review"
        ? "border-teal-200 shadow-e3"
        : "border-border shadow-e1";

  return (
    <GateCanvas testId="installation-gate">
      <section
        className={`flex w-[430px] max-w-full flex-col gap-4 rounded-[13px] border bg-card p-[26px] ${cardClass}`}
      >
        {state === "new" && (
          <>
            <span className="grid size-11 place-items-center rounded-lg bg-canvas text-muted-foreground">
              <DeviceIcon />
            </span>
            <h1 className="font-heading text-base font-bold leading-[1.4]">
              {t("entry.dev1Title")}
            </h1>
            <p className="text-[12.5px] leading-[1.8] text-muted-foreground">
              {t("entry.dev1Body")}
            </p>
            <div className="mt-auto flex flex-col gap-[9px]">
              <FingerprintBlock label={t("entry.deviceFingerprint")} value={fingerprint} />
              <Button
                className="h-[46px] rounded-[9px]"
                disabled={gate.busy}
                onClick={() => void gate.register()}
              >
                {t("entry.registerDevice")}
              </Button>
            </div>
          </>
        )}

        {state === "waiting" && (
          <>
            <span className="grid size-11 place-items-center rounded-lg bg-warning-soft motion-safe:animate-pulse-ring">
              <span className="block size-[19px] rounded-full border-[2.4px] border-warning-border border-t-warning motion-safe:animate-spin" />
            </span>
            <h1 className="font-heading text-base font-bold leading-[1.4]">
              {t("entry.dev2Title")}
            </h1>
            <p className="text-[12.5px] leading-[1.8] text-muted-foreground">
              {t("entry.dev2Body")}
            </p>
            <div className="mt-auto flex flex-col gap-[9px]">
              <FingerprintBlock label={t("entry.deviceFingerprint")} value={fingerprint} />
              <Badge variant="warning" dot className="w-full justify-start px-[14px]">
                {t("entry.requestSent")}
              </Badge>
              <Button
                variant="outline"
                className="h-[46px] rounded-[9px]"
                disabled={gate.checking}
                onClick={() => void gate.checkAgain()}
              >
                {t("entry.checkAgain")}
              </Button>
            </div>
          </>
        )}

        {state === "review" && (
          <>
            <span className="grid size-11 place-items-center rounded-lg bg-teal-50 text-primary">
              <RequesterIcon />
            </span>
            <h1 className="font-heading text-base font-bold leading-[1.4]">
              {t("entry.dev3Title")}
            </h1>
            <p className="text-[12.5px] leading-[1.8] text-muted-foreground">
              {t("entry.dev3Body")}
            </p>
            <div className="mt-auto flex flex-col gap-[9px]">
              <div className="flex flex-col gap-1.5 rounded-[9px] border border-sunken bg-surface-2 px-[14px] py-[13px] text-[11.5px] text-muted-foreground">
                <span className="flex items-center justify-between gap-3">
                  {t("entry.requester")}
                  <span className="font-heading font-bold text-foreground">
                    {locale === "ar" ? user.displayNameAr : user.displayNameFr}
                  </span>
                </span>
                <span className="flex items-center justify-between gap-3">
                  {t("entry.deviceFingerprint")}
                  <bdi dir="ltr" className="chams-figure font-semibold text-foreground">
                    {fingerprint}
                  </bdi>
                </span>
              </div>
              <div className="flex gap-[9px]">
                <Button
                  className="h-[46px] flex-1 rounded-[9px]"
                  disabled={gate.busy}
                  onClick={() => void gate.authorize()}
                >
                  {t("entry.approve")}
                </Button>
                <Button
                  variant="destructive"
                  className="h-[46px] rounded-[9px] px-4"
                  disabled={gate.busy}
                  onClick={onLogout}
                >
                  {t("entry.reject")}
                </Button>
              </div>
            </div>
          </>
        )}

        {state === "error" && (
          <>
            <span className="grid size-11 place-items-center rounded-lg bg-[#fdf0ef] font-heading text-[21px] font-extrabold text-destructive">
              !
            </span>
            <h1 className="font-heading text-base font-bold leading-[1.4]">
              {t("entry.dev4Title")}
            </h1>
            <p className="text-[12.5px] leading-[1.8] text-muted-foreground">
              {t("entry.dev4Body")}
            </p>
            <div className="mt-auto flex flex-col gap-[9px]">
              <FingerprintBlock label={t("entry.deviceFingerprint")} value={fingerprint} />
              <div className="rounded-[9px] border border-destructive-border bg-destructive-soft px-[14px] py-3 text-[11.5px] leading-[1.6] text-destructive">
                {t("entry.dev4Detail")}
              </div>
              <Button
                variant="outline"
                className="h-[46px] rounded-[9px]"
                disabled={gate.busy}
                onClick={() => void (gate.installation ? gate.authorize() : gate.register())}
              >
                {t("entry.retry")}
              </Button>
            </div>
          </>
        )}
      </section>
    </GateCanvas>
  );
}

/* ------------------------------------------------------------------ */
/* Offline setup                                                       */
/* ------------------------------------------------------------------ */

const PIN_LENGTH = 8;

/** Presentational rule only — the local store stays the authority on the PIN. */
export function looksPredictable(pin: string): boolean {
  if (pin.length < PIN_LENGTH) return false;
  const digits = [...pin].map(Number);
  const repeated = digits.every((digit) => digit === digits[0]);
  const ascending = digits.every((digit, index) => index === 0 || digit === digits[index - 1]! + 1);
  const descending = digits.every(
    (digit, index) => index === 0 || digit === digits[index - 1]! - 1,
  );
  return repeated || ascending || descending;
}

function RuleRow({ met, children }: { readonly met: boolean; readonly children: ReactNode }) {
  return (
    <span
      className={`flex items-center gap-2 text-[11.5px] leading-[1.5] ${met ? "text-success" : "text-muted-2"}`}
    >
      {met ? (
        <span
          aria-hidden="true"
          className="grid size-[15px] place-items-center rounded-full bg-success font-heading text-[9px] font-extrabold text-white"
        >
          ✓
        </span>
      ) : (
        <span aria-hidden="true" className="size-[15px] rounded-full border-[1.5px] border-input" />
      )}
      {children}
    </span>
  );
}

function PinCells({
  id,
  label,
  value,
  onChange,
  showLabel,
  hideLabel,
  autoFocus = false,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly showLabel: string;
  readonly hideLabel: string;
  readonly autoFocus?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const cells = Array.from({ length: PIN_LENGTH }, (_, index) => index);

  return (
    <div className="flex flex-col gap-[9px]">
      <Label
        htmlFor={id}
        className="font-heading text-[11.5px] font-bold text-muted-foreground text-start"
      >
        {label}
      </Label>
      <div className="flex items-stretch gap-2">
        <div className="relative min-w-0 flex-1">
          <input
            id={id}
            dir="ltr"
            type={revealed ? "text" : "password"}
            inputMode="numeric"
            pattern="[0-9]{8}"
            maxLength={PIN_LENGTH}
            autoComplete="new-password"
            autoFocus={autoFocus}
            required
            value={value}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(event) =>
              onChange(event.target.value.replaceAll(/\D/g, "").slice(0, PIN_LENGTH))
            }
            className="absolute inset-0 z-10 size-full cursor-text opacity-0"
          />
          <div dir="ltr" aria-hidden="true" className="flex gap-[7px]">
            {cells.map((index) => {
              const filled = index < value.length;
              const active = focused && index === Math.min(value.length, PIN_LENGTH - 1);
              return (
                <span
                  key={index}
                  className={`grid h-[52px] flex-1 place-items-center rounded-[9px] border font-heading text-lg font-bold ${
                    active
                      ? "border-primary bg-card outline outline-2 outline-offset-1 outline-teal-200"
                      : "border-input bg-surface-2"
                  }`}
                >
                  {filled ? (
                    revealed ? (
                      value[index]
                    ) : (
                      "•"
                    )
                  ) : active ? (
                    <span className="h-5 w-0.5 bg-primary" />
                  ) : null}
                </span>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          aria-label={revealed ? hideLabel : showLabel}
          aria-pressed={revealed}
          onClick={() => setRevealed((current) => !current)}
          className="chams-focusable grid size-[52px] shrink-0 place-items-center self-end rounded-[9px] border border-input bg-card text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        >
          {revealed ? (
            <EyeOff aria-hidden="true" className="size-[18px]" />
          ) : (
            <Eye aria-hidden="true" className="size-[18px]" />
          )}
        </button>
      </div>
    </div>
  );
}

export function DesktopOfflineSetupScreen({
  locale,
  passkey,
  onReady,
}: {
  readonly locale: Locale;
  readonly passkey: PasskeyUnlockMaterial | null;
  readonly onReady: (replica: StudentReplicaContract) => void;
}) {
  const t = (key: TranslationKey) => translate(locale, key);
  const setup = useOfflineSetup(onReady, passkey);

  return (
    <GateCanvas testId="offline-setup">
      <form
        className="gate-form flex w-[470px] max-w-full animate-fade-up flex-col gap-3"
        onSubmit={(event) => stop(event, () => void setup.submit())}
      >
        <section className="flex flex-col gap-[18px] rounded-[13px] border border-border bg-card p-7 shadow-e1">
          <div className="flex items-center gap-3">
            <span className="grid size-[38px] place-items-center rounded-[10px] bg-teal-50 text-primary">
              <LockIcon />
            </span>
            <span className="flex flex-col gap-[3px]">
              <h1 className="font-heading text-base font-bold leading-[1.3]">
                {t("entry.pinSetupTitle")}
              </h1>
              <span className="text-[11.5px] leading-[1.5] text-muted-2">
                {t("entry.pinSetupSub")}
              </span>
            </span>
          </div>
          <p className="text-[12.5px] leading-[1.8] text-muted-foreground">
            {t("entry.pinSetupBody")}
          </p>

          <PinCells
            id="offline-pin"
            label={t("entry.pinNew")}
            value={setup.pin}
            onChange={setup.setPin}
            showLabel={t("entry.pinShow")}
            hideLabel={t("entry.pinHide")}
            autoFocus
          />
          <PinCells
            id="offline-pin-confirm"
            label={t("entry.pinConfirm")}
            value={setup.confirm}
            onChange={setup.setConfirm}
            showLabel={t("entry.pinShow")}
            hideLabel={t("entry.pinHide")}
          />

          <div className="flex flex-col gap-[7px] rounded-[10px] border border-sunken bg-surface-2 px-[15px] py-[13px]">
            <RuleRow met={setup.pin.length === PIN_LENGTH}>{t("entry.pinRule1")}</RuleRow>
            <RuleRow met={setup.pin.length === PIN_LENGTH && !looksPredictable(setup.pin)}>
              {t("entry.pinRule2")}
            </RuleRow>
          </div>

          <Button type="submit" size="lg" disabled={setup.busy} className="w-full rounded-[10px]">
            {t("entry.pinSave")}
          </Button>
        </section>

        {setup.mismatch && (
          <div className="flex animate-shake flex-col gap-[5px] rounded-[11px] border border-destructive-border bg-destructive-soft px-4 py-[14px] text-destructive">
            <span className="font-heading text-[12.5px] font-bold leading-[1.4]">
              {t("entry.pinMismatch")}
            </span>
            <span className="text-[11px] leading-[1.6]">{t("entry.pinMismatchBody")}</span>
          </div>
        )}
        {setup.busy && (
          <div className="flex flex-col gap-[7px] rounded-[11px] border border-teal-100 bg-teal-50 px-4 py-[14px]">
            <span className="font-heading text-[12.5px] font-bold leading-[1.4] text-primary-strong">
              {t("entry.pinPreparing")}
            </span>
            <span className="h-[5px] overflow-hidden rounded-[3px] bg-teal-100">
              <span className="block h-full w-2/5 rounded-[3px] bg-primary motion-safe:animate-pulse" />
            </span>
          </div>
        )}
      </form>
    </GateCanvas>
  );
}

/* ------------------------------------------------------------------ */
/* Locked                                                              */
/* ------------------------------------------------------------------ */

export function PinDots({
  length,
  tone,
}: {
  readonly length: number;
  readonly tone: "light" | "dark";
}) {
  return (
    <>
      {Array.from({ length: PIN_LENGTH }, (_, index) => index).map((index) => {
        const filled = index < length;
        return (
          <span
            key={index}
            aria-hidden="true"
            className={`rounded-full transition-[width,height] duration-fast ${
              filled
                ? `size-[13px] animate-pop ${tone === "dark" ? "bg-white" : "bg-primary"}`
                : `size-[10px] ${tone === "dark" ? "bg-white/25" : "bg-border"}`
            }`}
          />
        );
      })}
    </>
  );
}

export function Keypad({
  tone,
  disabled,
  onDigit,
  onBackspace,
  backspaceLabel,
  passkeyBusy = false,
  onPasskey,
  passkeyLabel,
}: {
  readonly tone: "light" | "dark";
  readonly disabled: boolean;
  readonly onDigit: (digit: string) => void;
  readonly onBackspace: () => void;
  readonly backspaceLabel: string;
  readonly passkeyBusy?: boolean;
  readonly onPasskey?: () => void;
  readonly passkeyLabel?: string;
}) {
  const keyClass = `grid min-h-16 place-items-center rounded-[18px] font-heading text-2xl font-semibold outline-none transition-colors duration-fast focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
    tone === "dark"
      ? "bg-white/10 text-white focus-visible:outline-white"
      : "bg-sunken text-foreground focus-visible:outline-ring"
  }`;

  return (
    <div dir="ltr" className="grid grid-cols-3 gap-3.5">
      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
        <button
          key={digit}
          type="button"
          disabled={disabled}
          onClick={() => onDigit(digit)}
          className={`${keyClass} chams-figure`}
        >
          {digit}
        </button>
      ))}
      {onPasskey && passkeyLabel ? (
        <button
          type="button"
          disabled={disabled || passkeyBusy}
          onClick={onPasskey}
          aria-label={passkeyLabel}
          className={`${keyClass} bg-transparent text-[22px]`}
        >
          <Fingerprint
            aria-hidden="true"
            className={passkeyBusy ? "size-6 animate-pulse" : "size-6"}
          />
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => onDigit("0")}
        className={`${keyClass} chams-figure`}
      >
        0
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={onBackspace}
        aria-label={backspaceLabel}
        className={`${keyClass} bg-transparent text-[19px]`}
      >
        ⌫
      </button>
    </div>
  );
}

/**
 * Shared unlock behaviour for both presentations: keypad writes, one shake per
 * rejected attempt, and the terminal reason the store reported.
 */
export function useLockedScreen(unlock: ReturnType<typeof useUnlock>): {
  readonly wrong: boolean;
  readonly attempt: number;
  readonly press: (digit: string) => void;
  readonly backspace: () => void;
} {
  const [attempt, setAttempt] = useState(0);
  const busyBefore = useRef(false);
  const { busy, error, passkeyBusy, pin, setPin, submit } = unlock;

  useEffect(() => {
    if (busyBefore.current && !busy && error) setAttempt((value) => value + 1);
    busyBefore.current = busy;
  }, [busy, error]);

  useEffect(() => {
    if (attempt > 0) setPin("");
  }, [attempt, setPin]);

  useEffect(() => {
    if (pin.length === PIN_LENGTH && !busy && !passkeyBusy) void submit();
  }, [busy, passkeyBusy, pin, submit]);

  return {
    wrong: attempt > 0 && Boolean(error),
    attempt,
    press: (digit: string) => {
      if (pin.length < PIN_LENGTH) setPin(pin + digit);
    },
    backspace: () => setPin(pin.slice(0, -1)),
  };
}

export function LockedTerminalCard({
  tone,
  icon,
  title,
  body,
  detail,
  children,
}: {
  readonly tone: "warning" | "destructive";
  readonly icon: ReactNode;
  readonly title: string;
  readonly body: string;
  readonly detail?: string;
  readonly children: ReactNode;
}) {
  return (
    <section
      className={`flex w-[430px] max-w-full animate-fade-up flex-col gap-3.5 rounded-[13px] border bg-card p-6 shadow-e1 ${
        tone === "warning" ? "border-border" : "border-destructive-border"
      }`}
    >
      <span
        className={`grid size-10 place-items-center rounded-[11px] ${
          tone === "warning" ? "bg-warning-soft text-warning" : "bg-[#fdf0ef] text-destructive"
        }`}
      >
        {icon}
      </span>
      <h1 className="font-heading text-base font-bold leading-[1.4]">{title}</h1>
      <p className="text-[12.5px] leading-[1.8] text-muted-foreground">{body}</p>
      {detail && (
        <p className="rounded-[9px] border border-destructive-border bg-destructive-soft px-[14px] py-3 text-[11.5px] leading-[1.6] text-destructive">
          <bdi dir="ltr" className="chams-figure">
            {detail}
          </bdi>
        </p>
      )}
      {children}
    </section>
  );
}

export function DesktopLockedScreen({
  locale,
  onlineReauthorized,
  pendingPasskey,
  onUnlocked,
  onOnlineSignIn,
}: {
  readonly locale: Locale;
  readonly onlineReauthorized: boolean;
  readonly pendingPasskey: PasskeyUnlockMaterial | null;
  readonly onUnlocked: (replica: StudentReplicaContract) => void | Promise<void>;
  readonly onOnlineSignIn: () => void;
}) {
  const t = (key: TranslationKey) => translate(locale, key);
  const unlock = useUnlock(locale, onlineReauthorized, onUnlocked, pendingPasskey);
  const keypad = useLockedScreen(unlock);

  if (unlock.errorCode === "ONLINE_REAUTHORIZATION_REQUIRED")
    return (
      <GateCanvas testId="locked-gate">
        <LockedTerminalCard
          tone="warning"
          icon={<ClockIcon />}
          title={t("entry.lockoutTitle")}
          body={t("entry.lockoutBody")}
        >
          <Button className="h-[46px] rounded-[9px]" onClick={onOnlineSignIn}>
            {t("entry.signInOnline")}
          </Button>
        </LockedTerminalCard>
      </GateCanvas>
    );

  if (unlock.errorCode === "CORRUPT_CACHE")
    return (
      <GateCanvas testId="locked-gate">
        <LockedTerminalCard
          tone="destructive"
          icon={<BrokenBoxIcon />}
          title={t("entry.corruptTitle")}
          body={t("entry.corruptBody")}
          detail="CORRUPT_CACHE"
        >
          <div className="flex gap-[9px]">
            <Button className="h-[46px] flex-1 rounded-[9px]" onClick={onOnlineSignIn}>
              {t("entry.rebuildCopy")}
            </Button>
            <Button
              variant="outline"
              className="h-[46px] rounded-[9px] px-4"
              onClick={onOnlineSignIn}
            >
              {t("entry.signInOnline")}
            </Button>
          </div>
        </LockedTerminalCard>
      </GateCanvas>
    );

  return (
    <GateCanvas testId="locked-gate">
      <section className="flex w-[380px] max-w-full animate-fade-up flex-col items-center gap-4 rounded-[13px] border border-border bg-card p-7 shadow-e1">
        <span aria-hidden="true" className="chams-logo size-11 bg-primary" />
        <h1 className="font-heading text-[19px] font-bold leading-[1.3]">
          {t("entry.lockedTitle")}
        </h1>
        <p className="max-w-[30ch] text-center text-[12.5px] leading-[1.6] text-muted-foreground">
          {keypad.wrong ? t("entry.lockSubWrong") : t("entry.lockSubIdle")}
        </p>

        <div className="relative w-full">
          <input
            dir="ltr"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{8}"
            maxLength={PIN_LENGTH}
            autoComplete="off"
            autoFocus
            aria-label={t("entry.lockedTitle")}
            value={unlock.pin}
            onChange={(event) =>
              unlock.setPin(event.target.value.replaceAll(/\D/g, "").slice(0, PIN_LENGTH))
            }
            className="absolute inset-0 z-10 size-full rounded-md bg-transparent text-transparent caret-transparent outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          />
          <div
            key={keypad.attempt}
            dir="ltr"
            className={`flex h-5 items-center justify-center gap-3 ${keypad.wrong ? "animate-shake" : ""}`}
          >
            <PinDots length={unlock.pin.length} tone="light" />
          </div>
        </div>

        <p
          role="status"
          className={`font-heading text-xs font-bold text-destructive transition-opacity duration-base ${
            keypad.wrong ? "opacity-100" : "opacity-0"
          }`}
        >
          {unlock.error ?? t("entry.wrongPin")}
        </p>

        <div className="w-full">
          <Keypad
            tone="light"
            disabled={unlock.busy || unlock.passkeyBusy}
            onDigit={keypad.press}
            onBackspace={keypad.backspace}
            backspaceLabel={t("sp.back")}
            passkeyBusy={unlock.passkeyBusy}
            onPasskey={() => void unlock.submitPasskey()}
            passkeyLabel={t("entry.usePasskey")}
          />
        </div>

        <Button
          variant="outline"
          className="h-[50px] w-full rounded-[14px]"
          onClick={onOnlineSignIn}
        >
          {t("entry.useAnotherAccount")}
        </Button>
      </section>
    </GateCanvas>
  );
}
